import React from 'react';
import { Link, useParams } from 'react-router';
import {
  ArrowLeft,
  Clock,
  Globe,
  IdCard,
  Phone,
  CheckCircle,
  XCircle,
  MessageSquare,
  RefreshCw,
  UserPlus,
  Download,
  RotateCcw,
  Mail,
  Bell,
} from 'lucide-react';
import { toast } from 'sonner';
import { CallStatus } from './components/CallStatus';
import {
  useGetOutreachCandidatesByIdQuery,
  useResetOutreachCandidateStatusMutation,
} from 'rtk/query/apis/outreachCandidates';
import { useGetOutreachCampaignByIdQuery, useGetOutreachTemplateByIdQuery } from 'rtk/query/apis/outreach';
import {
  useGetVoiceConversationByIdQuery,
  useReparseVoiceConversationMutation,
} from 'rtk/query/apis/voiceConversation/index.ts';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { HubertButton } from '@/components/HubertUI/HubertButton';
import { generateSingleCandidateCSV } from 'utils/exportUtils';
import FullViewLoading from 'components/common/FullViewLoading';
import type { TOutreachCandidate } from '@/types/outreachCandidates.types';

const ResetStatusButton: React.FC<{ referenceId: string }> = ({ referenceId }) => {
  const [resetStatus, { isLoading }] = useResetOutreachCandidateStatusMutation();

  const handleReset = async () => {
    if (!window.confirm("Are you sure you want to reset this candidate's status? History will be preserved.")) {
      return;
    }
    try {
      await resetStatus(referenceId).unwrap();
      toast.success('Candidate status reset successfully');
    } catch (error) {
      toast.error('Failed to reset candidate status');
      console.error(error);
    }
  };

  return (
    <HubertButton
      variant="destructive"
      size="sm"
      onClick={handleReset}
      disabled={isLoading}
      className="bg-orange-600 hover:bg-orange-700"
      iconLeft={<RotateCcw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />}
    >
      Reset Status (DEV ONLY)
    </HubertButton>
  );
};

const ReparseButton: React.FC<{ referenceId: string }> = ({ referenceId }) => {
  const [reparse, { isLoading }] = useReparseVoiceConversationMutation();

  const handleReparse = async () => {
    try {
      await reparse(referenceId).unwrap();
      toast.success('Conversation reparsed successfully');
    } catch (error) {
      toast.error('Failed to reparse conversation');
      console.error(error);
    }
  };

  return (
    <HubertButton
      variant="outline"
      size="sm"
      onClick={handleReparse}
      disabled={isLoading}
      className="hover:bg-slate-100"
      iconLeft={<RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />}
    >
      Reparse Data
    </HubertButton>
  );
};

interface CommunicationLogProps {
  activityLog?: TOutreachCandidate['activityLog'];
}

const CommunicationLog: React.FC<CommunicationLogProps> = ({ activityLog }) => {
  const communicationLog = React.useMemo(() => {
    const logs: { type: string; time: string; description: string; icon: React.JSX.Element; timestamp: number }[] = [];

    if (activityLog) {
      activityLog.forEach((log) => {
        const date = new Date(log.createdAt);
        const timeStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
          date.getDate(),
        ).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

        let icon = <MessageSquare className="h-4 w-4" />;
        let description = 'Activity';
        let type = 'Activity';

        switch (log.actionType) {
          case 'sms':
            type = 'SMS';
            switch (log.actionSubtype) {
              case 'scheduled':
                type = 'SMS Scheduled';
                description = 'Message scheduled for sending';
                icon = <Clock className="h-4 w-4 text-blue-500" />;
                break;
              case 'delivered':
                type = 'SMS Delivered';
                description = 'Message delivered successfully';
                icon = <CheckCircle className="h-4 w-4 text-green-500" />;
                break;
              case 'failed':
                type = 'SMS Failed';
                description = 'Message delivery failed';
                icon = <XCircle className="h-4 w-4 text-red-500" />;
                break;
              case 'invitation_sent':
                type = 'SMS Invitation';
                description = 'Invitation SMS sent';
                icon = <MessageSquare className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_sent':
                type = 'SMS Reminder';
                description = 'Reminder SMS sent';
                icon = <Bell className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_first_reminder_to_start':
                type = 'SMS First Reminder to Start';
                description = log.reason || 'First reminder to start SMS sent';
                icon = <Bell className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_second_reminder_to_start':
                type = 'SMS Second Reminder to Start';
                description = log.reason || 'Second reminder to start SMS sent';
                icon = <Bell className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_first_reminder_to_finish':
                type = 'SMS First Reminder to Finish';
                description = log.reason || 'First reminder to finish SMS sent';
                icon = <Bell className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_second_reminder_to_finish':
                type = 'SMS Second Reminder to Finish';
                description = log.reason || 'Second reminder to finish SMS sent';
                icon = <Bell className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_final_call_to_start':
                type = 'SMS Final Call to Start';
                description = log.reason || 'Final call to start SMS sent';
                icon = <Bell className="h-4 w-4 text-blue-500" />;
                break;
              default:
                description = `SMS: ${log.actionSubtype}`;
                icon = <MessageSquare className="h-4 w-4 text-blue-500" />;
            }
            break;

          case 'email':
            type = 'Email';
            switch (log.actionSubtype) {
              case 'scheduled':
                type = 'Email Scheduled';
                description = 'Email scheduled for sending';
                icon = <Clock className="h-4 w-4 text-blue-500" />;
                break;
              case 'invitation_sent':
                type = 'Email Invitation';
                description = 'Invitation email sent';
                icon = <Mail className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_sent':
                type = 'Email Reminder';
                description = 'Reminder email sent';
                icon = <Bell className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_first_reminder_to_start':
                type = 'Email First Reminder to Start';
                description = log.reason || 'First reminder to start email sent';
                icon = <Bell className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_second_reminder_to_start':
                type = 'Email Second Reminder to Start';
                description = log.reason || 'Second reminder to start email sent';
                icon = <Bell className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_first_reminder_to_finish':
                type = 'Email First Reminder to Finish';
                description = log.reason || 'First reminder to finish email sent';
                icon = <Bell className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_second_reminder_to_finish':
                type = 'Email Second Reminder to Finish';
                description = log.reason || 'Second reminder to finish email sent';
                icon = <Bell className="h-4 w-4 text-blue-500" />;
                break;
              case 'failed':
                type = 'Email Failed';
                description = 'Email delivery failed';
                icon = <XCircle className="h-4 w-4 text-red-500" />;
                break;
              default:
                description = `Email: ${log.actionSubtype}`;
                icon = <Mail className="h-4 w-4 text-blue-500" />;
            }
            break;

          case 'email_update': {
            let emailLabel = 'Email';
            if (log.tags && Array.isArray(log.tags)) {
              if (log.tags.includes('prescreening_invite')) {
                emailLabel = 'Invitation Email';
              } else {
                const reminderTags = [
                  'first_reminder_to_start',
                  'second_reminder_to_start',
                  'first_reminder_to_finish',
                  'second_reminder_to_finish',
                  'final_call_to_start',
                ];
                const foundTag = log.tags.find((tag) => reminderTags.includes(tag));
                if (foundTag) {
                  if (foundTag === 'first_reminder_to_start') emailLabel = 'First Reminder Email';
                  else if (foundTag === 'second_reminder_to_start') emailLabel = 'Second Reminder Email';
                  else if (foundTag === 'first_reminder_to_finish') emailLabel = 'First Reminder to Finish Email';
                  else if (foundTag === 'second_reminder_to_finish') emailLabel = 'Second Reminder to Finish Email';
                  else if (foundTag === 'final_call_to_start') emailLabel = 'Final Call Email';
                }
              }
            }

            type = emailLabel;
            description = log.message || `Email status updated to ${log.status || 'unknown'}`;
            icon = <Mail className="h-4 w-4 text-blue-500" />;

            switch (log.status) {
              case 'request':
              case 'scheduled':
                type = `${emailLabel} Scheduled`;
                icon = <Clock className="h-4 w-4 text-blue-500" />;
                break;
              case 'delivered':
                type = `${emailLabel} Delivered`;
                icon = <CheckCircle className="h-4 w-4 text-green-500" />;
                break;
              case 'open':
              case 'first_open':
                type = `${emailLabel} Opened`;
                icon = <Mail className="h-4 w-4 text-green-500" />;
                break;
              case 'click':
                type = `${emailLabel} Clicked`;
                icon = <CheckCircle className="h-4 w-4 text-green-500" />;
                break;
              case 'deferred':
                type = `${emailLabel} Deferred`;
                icon = <Clock className="h-4 w-4 text-orange-500" />;
                break;
              case 'bounce':
              case 'soft_bounce':
              case 'spam':
              case 'blocked':
              case 'invalid_email':
                type = `${emailLabel} Failed`;
                icon = <XCircle className="h-4 w-4 text-red-500" />;
                break;
            }
            break;
          }

          case 'system':
            type = 'System';
            switch (log.actionSubtype) {
              case 'scheduled':
                type = 'Call Scheduled';
                description = log.reason || 'Call scheduled';
                icon = <Clock className="h-4 w-4 text-blue-500" />;
                break;
              case 'rescheduled':
                type = 'Call Rescheduled';
                description = log.reason || 'Call rescheduled';
                icon = <RefreshCw className="h-4 w-4 text-orange-500" />;
                break;
              case 'call_initiated':
                type = 'Call Initiated';
                description = log.reason || 'System initiated a call';
                icon = <Phone className="h-4 w-4 text-blue-500" />;
                break;
              case 'chat_initiated':
                type = 'Chat Initiated';
                description = log.reason || 'Candidate initiated a chat';
                icon = <MessageSquare className="h-4 w-4 text-blue-500" />;
                break;
              case 'call_completed':
                type = 'Call Completed';
                description = log.reason || 'Call finished';
                icon = log.isSuccess ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <XCircle className="h-4 w-4 text-orange-500" />
                );
                break;
              case 'call_status_change':
                type = 'Call Status Change';
                description = `Call status changed from ${log.metadata?.oldStatus || '-'} to ${log.metadata?.newStatus || '-'}`;
                if (typeof log.metadata?.reason === 'string') description += ` (${log.metadata.reason})`;
                icon = <Phone className="h-4 w-4 text-blue-500" />;
                break;
              case 'qualification_status_change':
                type = 'Qualification Status Change';
                description = `Qualification status changed from ${log.metadata?.oldStatus || '-'} to ${log.metadata?.newStatus || '-'}`;
                if (typeof log.metadata?.reason === 'string') description += ` (${log.metadata.reason})`;
                icon = <IdCard className="h-4 w-4 text-purple-500" />;
                break;
              case 'candidate_created':
                type = 'Candidate Created';
                description = 'Candidate was created';
                icon = <UserPlus className="h-4 w-4 text-green-500" />;
                break;
              case 'reset':
                type = 'Candidate Reset';
                description = log.reason || 'Candidate was reset by admin';
                icon = <RotateCcw className="h-4 w-4 text-orange-600" />;
                break;
              case 'reminder_first_reminder_to_start':
                type = 'First Reminder to Start';
                description = log.reason || 'First reminder to start scheduled';
                icon = <Clock className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_second_reminder_to_start':
                type = 'Second Reminder to Start';
                description = log.reason || 'Second reminder to start scheduled';
                icon = <Clock className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_first_reminder_to_finish':
                type = 'First Reminder to Finish';
                description = log.reason || 'First reminder to finish scheduled';
                icon = <Clock className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_second_reminder_to_finish':
                type = 'Second Reminder to Finish';
                description = log.reason || 'Second reminder to finish scheduled';
                icon = <Clock className="h-4 w-4 text-blue-500" />;
                break;
              case 'reminder_final_call_to_start':
                type = 'Final Call Scheduled';
                description = log.reason || 'Final call to start scheduled';
                icon = <Clock className="h-4 w-4 text-blue-500" />;
                break;
              default:
                description = `System: ${log.actionSubtype}`;
                icon = <Clock className="h-4 w-4 text-blue-500" />;
            }
            break;
        }

        logs.push({
          type,
          time: timeStr,
          description,
          icon,
          timestamp: date.getTime(),
        });
      });
    }

    return logs.sort((a, b) => a.timestamp - b.timestamp);
  }, [activityLog]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Communication Log</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {communicationLog.map((log, index) => (
            <div key={`${log.timestamp}-${log.type}-${index}`} className="flex items-start gap-4">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-gray-100 p-2">{log.icon}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{log.type}</span>
                    <span className="text-muted-foreground text-sm">{log.time}</span>
                  </div>
                  <p className="text-muted-foreground text-sm">{log.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const OutreachCandidate: React.FC = () => {
  const { outreachCandidateId } = useParams();

  const { data: candidate, isLoading: candidateIsLoading } = useGetOutreachCandidatesByIdQuery(
    outreachCandidateId ?? '',
    {
      skip: !outreachCandidateId,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: voiceConversations, isLoading: conversationIsLoading } = useGetVoiceConversationByIdQuery(
    outreachCandidateId ?? '',
    {
      skip: !outreachCandidateId,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: campaign, isLoading: campaignIsLoading } = useGetOutreachCampaignByIdQuery(
    candidate?.campaignId ?? '',
    {
      skip: !candidate?.campaignId,
    },
  );

  const { data: template } = useGetOutreachTemplateByIdQuery(campaign?.outreachInterviewTemplateId ?? '', {
    skip: !campaign?.outreachInterviewTemplateId,
  });

  // Sort by date if needed, but assuming API returns chronological or we want the last in list
  const voiceConversation =
    voiceConversations && voiceConversations.length > 0 ? voiceConversations[voiceConversations.length - 1] : undefined;

  const [userSelectedLanguage, setUserSelectedLanguage] = React.useState<string | undefined>(undefined);

  const selectedLanguage = userSelectedLanguage || campaign?.defaultLanguage;

  // Extract questions from voice conversation
  const questions = React.useMemo(() => {
    const languageConfig =
      template && selectedLanguage ? template.languages.find((l) => l.language === selectedLanguage) : undefined;
    if (!voiceConversation?.questions) {
      return [];
    }
    return voiceConversation.questions.map((q) => {
      let questionText = q.text;
      let expectedAnswer = (q as { expectedAnswer?: boolean | string | number }).expectedAnswer;

      // Try to find matching question in selected language
      if (languageConfig) {
        const matchingQuestion = languageConfig.questions.find((lq) => lq.id === q.id);
        if (matchingQuestion) {
          questionText = matchingQuestion.text;
          if (matchingQuestion.expectedAnswer !== undefined) {
            expectedAnswer = matchingQuestion.expectedAnswer;
          }
        }
      }
      return {
        id: q.id,
        question: questionText,
        required: q.condition === 'required',
        expectedAnswer,
        answer: q.answer,
      };
    });
  }, [voiceConversation, template, selectedLanguage]);

  const pageIsLoading = candidateIsLoading || conversationIsLoading || campaignIsLoading;

  if (pageIsLoading) {
    return <FullViewLoading title="Loading candidate details..." />;
  }

  if (!candidate) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-center">
        <div className="rounded-full bg-red-100 p-4">
          <XCircle className="h-8 w-8 text-red-600" />
        </div>
        <h2 className="mt-4 text-xl font-semibold">Candidate Not Found</h2>
        <p className="text-muted-foreground mt-2 max-w-md">We couldn't find the candidate you're looking for.</p>
      </div>
    );
  }

  // Format call time
  const callTime = voiceConversation?.createdAt
    ? (() => {
        const date = new Date(voiceConversation.createdAt);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(
          2,
          '0',
        )} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
      })()
    : '-';

  // Format call duration
  const callDuration =
    voiceConversation?.callDuration !== undefined
      ? (() => {
          const totalSeconds = voiceConversation.callDuration;
          const minutes = Math.floor(totalSeconds / 60);
          const seconds = totalSeconds % 60;
          return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        })()
      : '-';

  const interviewLanguage = voiceConversation?.languageUsed || candidate.language;
  console.log('questions');
  console.log(questions);
  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div>
        <Link
          to={`/outreach/campaigns/${candidate.campaignId}`}
          className="text-muted-foreground hover:text-foreground mb-4 flex items-center gap-2 text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Campaign
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">
                {candidate.firstName} {candidate.lastName}
              </h1>
              {candidate.qualificationStatus === 'QUALIFIED' && (
                <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">
                  Qualified{candidate.screeningScore != null ? ` (${candidate.screeningScore})` : ''}
                </Badge>
              )}
              {candidate.qualificationStatus === 'NOT_QUALIFIED' && (
                <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                  Not Qualified{candidate.screeningScore != null ? ` (${candidate.screeningScore})` : ''}
                </Badge>
              )}
              {candidate.qualificationStatus === 'PENDING' && (
                <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">Pending</Badge>
              )}

              {/* Call Status Badge */}
              {candidate.callStatus && <CallStatus status={candidate.callStatus} variant="badge" />}

              {voiceConversation && voiceConversation.channel === 'phone' && (
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                  <Phone className="mr-1 h-3 w-3" />
                  Completed via Phone
                </Badge>
              )}
              {voiceConversation && voiceConversation.channel === 'chat' && (
                <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
                  <MessageSquare className="mr-1 h-3 w-3" />
                  Completed via Chat
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-2">
              {campaign
                ? `${template?.languages.find((l) => l.language === campaign.defaultLanguage)?.role ?? ''} @ ${campaign.companyName}`
                : '-'}
            </p>
          </div>
          <div className="flex gap-2">
            <HubertButton
              variant="outline"
              size="sm"
              className="hover:bg-slate-100"
              iconLeft={<Download className="h-4 w-4" />}
              onClick={() => {
                if (candidate) {
                  const csvContent = generateSingleCandidateCSV(candidate, campaign, voiceConversations, template);
                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.setAttribute('href', url);
                  link.setAttribute('download', `candidate_${candidate.id}_full_profile.csv`);
                  link.style.visibility = 'hidden';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              }}
            >
              Export CSV
            </HubertButton>
            {outreachCandidateId && <ReparseButton referenceId={outreachCandidateId} />}
            {import.meta.env.DEV && outreachCandidateId && <ResetStatusButton referenceId={outreachCandidateId} />}
          </div>
        </div>

        {/* Metadata */}
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-blue-50 p-2">
              <Clock className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Call Time</div>
              <div className="text-sm font-medium">{callTime}</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-blue-50 p-2">
              <Phone className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Call Duration</div>
              <div className="text-sm font-medium">{callDuration}</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-blue-50 p-2">
              <Globe className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Primary Language</div>
              <div className="text-sm font-medium">{interviewLanguage}</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-blue-50 p-2">
              <IdCard className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Candidate ID</div>
              <div className="text-sm font-medium">{candidate.id.substring(0, 12)}...</div>
            </div>
          </div>
        </div>
      </div>

      {/* Call Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Call Analysis
            {voiceConversation?.parsedAt && (
              <span className="text-muted-foreground ml-auto text-xs font-normal">
                Last parsed:{' '}
                {(() => {
                  const date = new Date(voiceConversation.parsedAt);
                  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
                    date.getDate(),
                  ).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(
                    2,
                    '0',
                  )}`;
                })()}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Executive Summary */}
          <div>
            <h3 className="mb-2 text-sm font-semibold">Executive Summary</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {voiceConversation?.analysis?.transcriptSummary || 'No summary available for this conversation.'}
            </p>
          </div>

          {/* Call Status and Criteria */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Call Status */}
            <div>
              <h3 className="mb-3 text-sm font-semibold">Call Status</h3>
              <div className="space-y-2">
                <CallStatus status={candidate.callStatus} variant="card" />
              </div>
            </div>

            {/* Qualification Status */}
            <div>
              <h3 className="mb-3 text-sm font-semibold">Qualification Status</h3>
              <div className="space-y-2">
                <div
                  className={`flex items-center justify-between rounded-lg p-3 ${
                    candidate.qualificationStatus === 'QUALIFIED'
                      ? 'border border-green-200 bg-green-50'
                      : candidate.qualificationStatus === 'NOT_QUALIFIED'
                        ? 'border border-red-200 bg-red-50'
                        : 'border border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {candidate.qualificationStatus === 'QUALIFIED' ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : candidate.qualificationStatus === 'NOT_QUALIFIED' ? (
                      <XCircle className="h-5 w-5 text-red-600" />
                    ) : (
                      <Clock className="h-5 w-5 text-gray-500" />
                    )}
                    <span className="text-sm font-medium">
                      {candidate.qualificationStatus === 'QUALIFIED'
                        ? 'Qualified'
                        : candidate.qualificationStatus === 'NOT_QUALIFIED'
                          ? 'Not Qualified'
                          : 'Pending'}
                    </span>
                  </div>
                  {candidate.screeningScore !== undefined && candidate.screeningScore !== null && (
                    <span className="text-sm font-semibold text-gray-700">Score: {candidate.screeningScore}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Explanation */}
            <div className="rounded-lg bg-gray-50 p-4">
              <h3 className="mb-2 text-sm font-semibold">Explanation</h3>
              <p className="text-muted-foreground text-sm">
                {voiceConversation?.analysis?.evaluationCriteriaResults
                  ? Object.values(voiceConversation.analysis.evaluationCriteriaResults)[0]?.rationale ||
                    'All required questions were asked and answered satisfactorily.'
                  : 'All required questions were asked and answered satisfactorily.'}
              </p>
            </div>

            {/* Consent */}
            <div className={`rounded-lg p-4 ${voiceConversation?.consentGiven ? 'bg-green-50' : 'bg-red-50'}`}>
              <h3 className="mb-2 text-sm font-semibold text-gray-700">Consent to call recording</h3>
              <div className="flex items-center gap-2">
                {voiceConversation?.consentGiven ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-700">Consent Given</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-red-600" />
                    <span className="font-medium text-red-700">Consent not given</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Question-by-Question */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Question-by-Question</CardTitle>
          {template && template.languages.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">Language:</span>
              <select
                className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm focus:border-blue-500 focus:outline-none"
                value={selectedLanguage || ''}
                onChange={(e) => setUserSelectedLanguage(e.target.value)}
              >
                {template.languages.map((l) => {
                  let label = l.language;
                  if (l.language === campaign?.defaultLanguage) {
                    label += ' (Default)';
                  }
                  // Case insensitive check
                  if (interviewLanguage && l.language.toLowerCase() === interviewLanguage.toLowerCase()) {
                    label += ' (Interview Language)';
                  }
                  return (
                    <option key={l.language} value={l.language}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Question</TableHead>
                <TableHead className="w-24">Required</TableHead>
                <TableHead className="w-36">Expected Answer</TableHead>
                <TableHead>Candidate Answer</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {questions.length > 0 ? (
                questions.map((q) => (
                  <TableRow key={q.id}>
                    <TableCell className="font-medium">{q.question}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {q.required ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {q.expectedAnswer !== undefined ? (
                        <Badge variant="outline" className="text-xs">
                          {typeof q.expectedAnswer === 'boolean'
                            ? q.expectedAnswer
                              ? 'Yes'
                              : 'No'
                            : String(q.expectedAnswer)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {q.answer === null || q.answer.raw === '' ? (
                        <div>-</div>
                      ) : 'iso' in q.answer ? (
                        <>
                          <div>{q.answer.iso ?? '-'}</div>
                          <div className="text-muted-foreground mt-1 text-xs">Raw: {q.answer.raw}</div>
                        </>
                      ) : (
                        <>
                          <div>
                            {typeof q.answer.value === 'boolean' ? (q.answer.value ? 'Yes' : 'No') : q.answer.value}
                          </div>
                          <div className="text-muted-foreground mt-1 text-xs">Raw: {q.answer.raw}</div>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-muted-foreground text-center">
                    No questions available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Communication Log */}
      <CommunicationLog activityLog={candidate.activityLog} />
    </div>
  );
};
